import { useUserStore } from '@bv/stores';
import { generateBreadcrumbs } from '@bv/utils/route/breadcrumbs/generate-breadcrumbs';
import { computed } from 'vue';

import campaignUpgradeImage from '@/assets/images/routes/campaign/upgrade-campaign.png';


const callEvaluationBreadcrumbs = generateBreadcrumbs({
  label: 'global.aside.call_evaluation',
  route: { name: 'call-evaluation' },
});

const canAccessCallEvaluation = computed(() => {
  const store = useUserStore();
  return !!store.callCenterSettings?.is_call_evaluation_enabled;
});

const isCallEvaluationMigrated = computed(() => {
  const store = useUserStore();
  return !!store.callCenterSettings?.is_call_evaluation_migrated;
});

const canReadCallEvaluationSettings = computed(() => {
  const store = useUserStore();
  return store.permissions.includes('read-call-evaluation-settings');
});

const canReadCallEvaluationReports = computed(() => {
  const store = useUserStore();
  return store.permissions.includes('read-call-evaluation-reports');
});

const showCallEvaluationTab = computed(() => {
  const store = useUserStore();
  return !!store.callCenterSettings?.is_call_evaluation_enabled || store.isOwner;
});

const showCallEvaluationListTab = computed(() => {
  return showCallEvaluationTab.value && isCallEvaluationMigrated.value && canReadCallEvaluationSettings.value;
});

const showCallEvaluationReportTab = computed(() => {
  return showCallEvaluationTab.value && canReadCallEvaluationReports.value;
});

const callEvaluationRoutes = {
  path: '/call-evaluation',
  name: 'call-evaluation',
  meta: {
    redirectToFirstAccessibleChild: true,
    header: {
      title: 'global.aside.call_evaluation',
      icon: 'bvi-face-happy',
    },
    canAccess: canAccessCallEvaluation,
    redirectUnauthorized: { path: '/dashboard' },
    upgradeState: {
      image: campaignUpgradeImage,
      title: 'callEvaluation.upgrade.title',
      description: 'callEvaluation.upgrade.description',
    },
    breadcrumbs: callEvaluationBreadcrumbs(),
    sidebar: [
      {
        title: 'callEvaluation.nav.reports',
        route: { name: 'call-evaluation-report' },
        icon: 'bvi-performance-square',
        permissions: 'read-call-evaluation-reports',
        visible: showCallEvaluationReportTab,
      },
      {
        title: 'callEvaluation.nav.list',
        route: { name: 'call-evaluation-list' },
        icon: 'bvi-menu',
        permissions: 'read-call-evaluation-settings',
        visible: showCallEvaluationListTab,
      },
    ],
  },
  children: [
    {
      path: 'report',
      name: 'call-evaluation-report',
      component: () => import('@/components/CallEvaluation/CallEvaluationReport.vue'),
      meta: {
        permissions: 'read-call-evaluation-reports',
        redirectUnauthorized: { name: 'call-evaluation-list' },
        breadcrumbs: callEvaluationBreadcrumbs({
          label: 'callEvaluation.nav.reports',
          route: { name: 'call-evaluation-report' },
        }),
      },
    },
    {
      path: 'list',
      name: 'call-evaluation-list',
      component: () => import('@/components/CallEvaluation/CallEvaluationList.vue'),
      meta: {
        permissions: 'read-call-evaluation-settings',
        redirectUnauthorized: { name: 'call-evaluation-report' },
        breadcrumbs: callEvaluationBreadcrumbs({
          label: 'callEvaluation.nav.list',
          route: { name: 'call-evaluation-list' },
        }),
      },
    },
    {
      path: 'add',
      name: 'call-evaluation-add',
      component: () => import('@/components/CallEvaluation/CallEvaluationForm.vue'),
      meta: {
        permissions: 'create-call-evaluation-settings',
        redirectUnauthorized: { name: 'call-evaluation-list' },
        breadcrumbs: callEvaluationBreadcrumbs({
          label: 'callEvaluation.nav.list',
          route: { name: 'call-evaluation-list' },
        }),
      },
    },
    {
      path: 'edit/:id',
      name: 'call-evaluation-edit',
      component: () => import('@/components/CallEvaluation/CallEvaluationForm.vue'),
      meta: {
        permissions: 'update-call-evaluation-settings',
        redirectUnauthorized: { name: 'call-evaluation-list' },
        breadcrumbs: callEvaluationBreadcrumbs({
          label: 'callEvaluation.nav.list',
          route: { name: 'call-evaluation-list' },
        }),
      },
    },
  ],
};

export default callEvaluationRoutes;
